import {
  SET_GLOBAL_STATISTICS,
  SET_VIDEOS_STATISTICS,
  SET_VIDEO_STATISTICS,
  SET_VIDEOS_RANK,
  SET_MESSAGES_STATISTICS,
  SET_MESSAGES_RANK,
  SET_USERS_RANK,
  SET_CUSTOM_RANK
} from ":mutations";

import {
  REQUEST_GLOBAL_STATISTICS,
  REQUEST_VIDEOS_STATISTICS,
  REQUEST_VIDEO_STATISTICS,
  REQUEST_VIDEOS_RANK,
  REQUEST_MESSAGES_STATISTICS,
  REQUEST_MESSAGES_RANK,
  REQUEST_USERS_RANK,
  REQUEST_CUSTOM_RANK
} from ":actions";

import http from ":http";

import { all } from "%utils/promises";

const setStatistics = field => (state, { total, daily, monthly }) => {
  if (total) {
    state[field].total = total;
  }

  if (daily) {
    state[field].daily = daily;
  }

  if (monthly) {
    state[field].monthly = monthly;
  }
};

const getDistinctValue = (distinct, fromState) => {
  if (distinct === undefined) {
    return fromState;
  }

  if (distinct === null) {
    return undefined;
  }

  return distinct;
};

const requestStatistics = (field, action, mutation) => (
  { state, commit },
  { total, daily, monthly }
) => {
  const dailyDistinct = getDistinctValue(
    daily && daily.distinct,
    state[field].daily.distinct
  );
  const monthlyDistinct = getDistinctValue(
    monthly && monthly.distinct,
    state[field].monthly.distinct
  );

  return all({
    total: total
      ? http.get(`/statistics`, {
          params: {
            groupBy: "action",
            action,
            ...total
          }
        })
      : Promise.resolve(),
    daily: daily
      ? http.get(`/statistics`, {
          params: {
            groupBy: "date,yyyy-mm-dd,Europe/Warsaw",
            action,
            sort: "key,desc",
            page: state[field].daily.number,
            distinct: dailyDistinct,
            ...daily
          }
        })
      : Promise.resolve(),
    monthly: monthly
      ? http.get(`/statistics`, {
          params: {
            groupBy: "date,yyyy-mm,Europe/Warsaw",
            action,
            sort: "key,desc",
            page: state[field].monthly.number,
            distinct: monthlyDistinct,
            ...monthly
          }
        })
      : Promise.resolve()
  }).then(response => {
    commit(mutation, {
      total: !!response.total && response.total.data.content[0].total,
      daily: !!response.daily && {
        ...response.daily.data,
        content: response.daily.data.content.reverse(),
        distinct: dailyDistinct
      },
      monthly: !!response.monthly && {
        ...response.monthly.data,
        content: response.monthly.data.content.reverse(),
        distinct: monthlyDistinct
      }
    });

    return response;
  });
};

const requestRank = (field, action, mutation, lookup) => (
  { state, commit },
  { page, size, distinct }
) => {
  distinct = getDistinctValue(distinct, state[field].distinct);

  return http
    .get("/statistics", {
      params: {
        groupBy: "resourceId",
        action,
        lookup,
        sort: "total,desc",
        ...{ page: state[field].number },
        page: page && page - 1,
        distinct,
        size
      }
    })
    .then(response =>
      commit(mutation, {
        rank: {
          ...response.data,
          page: response.data.number + 1,
          distinct
        }
      })
    );
};

const state = {
  global: {
    total: 0,
    daily: {
      content: [],
      distinct: null
    },
    monthly: {
      content: [],
      distinct: null
    }
  },

  videos: {
    total: 0,
    daily: {
      content: [],
      distinct: null
    },
    monthly: {
      content: [],
      distinct: null
    }
  },

  video: {
    total: 0,
    daily: {
      content: [],
      distinct: null
    },
    monthly: {
      content: [],
      distinct: null
    }
  },

  videosRank: {
    content: [],
    distinct: null
  },

  messages: {
    total: 0,
    daily: {
      content: [],
      distinct: null
    },
    monthly: {
      content: [],
      distinct: null
    }
  },

  messagesRank: {
    content: [],
    distinct: null
  },

  users: {
    byIp: {
      content: []
    },
    byUserAgent: {
      content: []
    }
  },

  customRank: {
    content: []
  }
};

const mutations = {
  [SET_GLOBAL_STATISTICS]: setStatistics("global"),
  [SET_VIDEOS_STATISTICS]: setStatistics("videos"),
  [SET_VIDEO_STATISTICS]: setStatistics("video"),
  [SET_MESSAGES_STATISTICS]: setStatistics("messages"),

  [SET_VIDEOS_RANK](state, { rank }) {
    state.videosRank = rank;
  },

  [SET_MESSAGES_RANK](state, { rank }) {
    state.messagesRank = rank;
  },

  [SET_USERS_RANK]({ users }, { ip, userAgent }) {
    if (ip) {
      users.byIp = ip;
    }

    if (userAgent) {
      users.byUserAgent = userAgent;
    }
  },

  [SET_CUSTOM_RANK](state, { rank }) {
    state.customRank = rank;
  }
};

const actions = {
  [REQUEST_GLOBAL_STATISTICS]: requestStatistics(
    "global",
    "GET_META_INFORMATION",
    SET_GLOBAL_STATISTICS
  ),
  [REQUEST_VIDEOS_STATISTICS]: requestStatistics(
    "videos",
    "GET_VIDEO",
    SET_VIDEOS_STATISTICS
  ),
  [REQUEST_MESSAGES_STATISTICS]: requestStatistics(
    "messages",
    "GET_MESSAGE",
    SET_MESSAGES_STATISTICS
  ),

  [REQUEST_VIDEO_STATISTICS]({ state, commit }, { total, daily, monthly, id }) {
    return requestStatistics("video", "GET_VIDEO", SET_VIDEO_STATISTICS)(
      { state, commit },
      {
        total: !!total && { ...total, resourceId: id },
        daily: !!daily && { ...daily, resourceId: id },
        monthly: !!monthly && { ...monthly, resourceId: id }
      }
    );
  },

  [REQUEST_VIDEOS_RANK]: requestRank(
    "videosRank",
    "GET_VIDEO",
    SET_VIDEOS_RANK,
    "video"
  ),
  [REQUEST_MESSAGES_RANK]: requestRank(
    "messagesRank",
    "GET_MESSAGE",
    SET_MESSAGES_RANK,
    "message"
  ),

  [REQUEST_USERS_RANK]({ state, commit }, { ip, userAgent }) {
    return all({
      ip: ip
        ? http.get("/statistics", {
            params: {
              groupBy: "address",
              sort: "total,desc",
              ...ip,
              page: ip.page ? ip.page - 1 : state.users.byIp.page - 1
            }
          })
        : Promise.resolve(),

      userAgent: userAgent
        ? http.get("/statistics", {
            params: {
              groupBy: "userAgent",
              sort: "total,desc",
              ...userAgent,
              page: userAgent.page
                ? userAgent.page - 1
                : state.users.byUserAgent.page - 1
            }
          })
        : Promise.resolve()
    }).then(response =>
      commit(SET_USERS_RANK, {
        ip: response.ip && {
          ...response.ip.data,
          page: response.ip.data.number + 1
        },
        userAgent: response.userAgent && {
          ...response.userAgent.data,
          page: response.userAgent.data.number + 1
        }
      })
    );
  },

  [REQUEST_CUSTOM_RANK]({ state, commit }, { query, page }) {
    return http
      .get("/statistics", {
        params: {
          ...JSON.parse(query),
          page: page ? page - 1 : state.customRank.page - 1
        }
      })
      .then(response => {
        commit(SET_CUSTOM_RANK, {
          rank: { ...response.data, page: response.data.number + 1 }
        });
        return response;
      });
  }
};

const getters = {};

export default {
  state,
  mutations,
  actions,
  getters
};
